import React from "react";
import { useNavigate } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import { PageHeader } from "@/components/common/PageHeader";
import { MetricCard } from "@/components/common/MetricCard";
import { StatusBadge } from "@/components/common/StatusBadge";
import { Panel, PanelHeader } from "@/components/common/Panel";
import { useFinanceTotals, money as ledgerMoney } from "@/components/finance/financeApi";
import { CauseListPanel } from "@/components/dashboard/CauseListPanel";
import { ConflictDashboardCard } from "@/components/dashboard/ConflictPanels";
import { ActivityFeed } from "@/components/dashboard/ActivityFeed";
import { query } from "@/services/services";
import { formatTime, formatDate, daysUntil } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { AlertTriangle, Clock, FileWarning, IndianRupee, ScrollText, Gavel, ArrowRight } from "lucide-react";

export default function Dashboard() {
  const { db, persona, isClient, clientScopeId, can } = useApp();
  const navigate = useNavigate();
  const financeTotals = useFinanceTotals();

  if (isClient) return <ClientDashboard db={db} clientScopeId={clientScopeId} persona={persona} navigate={navigate} />;

  const today = new Date().toDateString();
  const todaysHearings = db.hearings
    .filter((h) => new Date(h.date).toDateString() === today && !h.outcome)
    .sort((a, b) => new Date(a.date) - new Date(b.date));
  const upcomingHearings = db.hearings
    .filter((h) => !h.outcome && new Date(h.date) >= new Date())
    .sort((a, b) => new Date(a.date) - new Date(b.date));
  const displayHearings = todaysHearings.length ? todaysHearings : upcomingHearings.slice(0, 4);

  const tasksDue = db.tasks.filter((t) => t.status !== "Completed");
  const highPriorityTasks = tasksDue.filter((t) => t.priority === "High").length;
  const activeMatters = db.matters.filter((m) => !["Disposed", "Closed", "Archived"].includes(m.status));
  const pendingApprovals = db.drafts.filter((d) => d.status === "Under Review").length + db.automationRuns.filter((r) => r.status === "Awaiting Approval").length;

  const hour = new Date().getHours();
  const greeting = hour < 12 ? "Good morning" : hour < 17 ? "Good afternoon" : "Good evening";

  // Priority attention
  const priority = [];
  db.matters.forEach((m) => {
    const d = daysUntil(m.limitationDate);
    if (d != null && d >= 0 && d <= 5) priority.push({ icon: AlertTriangle, tone: "danger", title: `Limitation in ${d} day${d === 1 ? "" : "s"}`, sub: m.title, to: `/matters/${m.id}` });
  });
  db.tasks.filter((t) => t.limitationCritical && t.status !== "Completed").forEach((t) => { const m = query.matter(db, t.matterId); priority.push({ icon: AlertTriangle, tone: "danger", title: `Limitation-critical: ${t.title}`, sub: m?.title || "", to: `/matters/${t.matterId}` }); });
  db.drafts.filter((d) => d.status === "Under Review").forEach((d) => priority.push({ icon: FileWarning, tone: "warning", title: "Draft awaiting approval", sub: d.title, to: `/drafting/${d.id}` }));
  if (financeTotals?.overdue) priority.push({ icon: IndianRupee, tone: "danger", title: "Payment requests overdue", sub: `${ledgerMoney(financeTotals.overdue)} recorded outstanding`, to: "/billing" });
  db.orders.filter((o) => o.status === "In Progress").forEach((o) => { const m = query.matter(db, o.matterId); priority.push({ icon: ScrollText, tone: "warning", title: "Order compliance due", sub: m?.title || "", to: `/matters/${o.matterId}` }); });

  const recentMatters = [...activeMatters].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)).slice(0, 6);
  const monthStart = new Date(); monthStart.setDate(1); monthStart.setHours(0, 0, 0, 0);
  const newThisMonth = db.matters.filter((m) => m.createdAt && new Date(m.createdAt) >= monthStart).length;
  const pendingBreakdown = `${db.drafts.filter((d) => d.status === "Under Review").length} drafts · ${db.automationRuns.filter((r) => r.status === "Awaiting Approval").length} automation runs`;

  return (
    <div>
      <PageHeader title={`${greeting}, ${persona.name.split(" ")[0]}`} subtitle="Here is what requires your attention today.">
        {can("matter.create") && <Button variant="outline" onClick={() => navigate("/matters?ai=1")} data-testid="dashboard-ai-intake">AI Intake</Button>}
        {can("matter.create") && <Button onClick={() => navigate("/matters?new=1")} data-testid="dashboard-new-matter">New Matter</Button>}
      </PageHeader>

      {/* Metrics */}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-5">
        <MetricCard testid="metric-hearings" label="Hearings Today" value={todaysHearings.length || displayHearings.length} hint={`${db.hearings.filter(h => h.court.includes("High")).length} High Court · ${db.hearings.filter(h => !h.court.includes("High")).length} others`} tone="copper" icon={Gavel} onClick={() => navigate("/calendar")} />
        <MetricCard testid="metric-tasks" label="Tasks Due" value={tasksDue.length} hint={`${highPriorityTasks} high priority`} tone="warning" icon={Clock} onClick={() => navigate("/matters")} />
        <MetricCard testid="metric-matters" label="Active Matters" value={activeMatters.length} hint={`${newThisMonth} opened this month`} tone="info" onClick={() => navigate("/matters")} />
        <MetricCard testid="metric-approvals" label="Pending Approvals" value={pendingApprovals} hint={pendingBreakdown} tone="success" onClick={() => navigate("/drafting")} />
        <MetricCard testid="metric-revenue" label="Outstanding Requests" value={financeTotals ? ledgerMoney(financeTotals.outstanding) : '—'} hint="Verified financial subledger" tone="plain" icon={IndianRupee} onClick={() => navigate("/billing")} />
      </div>

      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          {/* Today's hearings */}
          <Panel>
            <PanelHeader title="Today's Hearings">
              <Button variant="ghost" size="sm" onClick={() => navigate("/calendar")}>View calendar</Button>
            </PanelHeader>
            {displayHearings.length === 0 ? (
              <div className="p-8 text-center text-sm text-ink-2">No hearings scheduled. A calm day for chamber work.</div>
            ) : (
              <div className="divide-y divide-line">
                {displayHearings.map((h) => {
                  const m = query.matter(db, h.matterId);
                  return (
                    <div key={h.id} className="flex items-center gap-4 px-4 py-3 hover:bg-surface" data-testid={`hearing-row-${h.id}`}>
                      <div className="w-16 shrink-0 text-center">
                        <div className="font-display text-lg font-semibold text-copper">{formatTime(h.date)}</div>
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="truncate font-medium text-ink">{m?.title}</div>
                        <div className="truncate text-xs text-ink-2">{m?.caseNumber} · {h.court} · {h.purpose}</div>
                      </div>
                      <Button variant="outline" size="sm" onClick={() => navigate(`/matters/${h.matterId}`)}>Hearing Brief</Button>
                    </div>
                  );
                })}
              </div>
            )}
          </Panel>

          <CauseListPanel />
          <ConflictDashboardCard />

          {/* Recent matters */}
          <Panel>
            <PanelHeader title="Recent Matters">
              <Button variant="ghost" size="sm" onClick={() => navigate("/matters")}>All matters</Button>
            </PanelHeader>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-line text-left text-xs uppercase tracking-wide text-ink-muted">
                    <th className="px-4 py-2 font-medium">Matter</th>
                    <th className="px-4 py-2 font-medium">Court</th>
                    <th className="px-4 py-2 font-medium">Stage</th>
                    <th className="px-4 py-2 font-medium">Next Date</th>
                    <th className="px-4 py-2 font-medium">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {recentMatters.map((m) => (
                    <tr key={m.id} className="cursor-pointer border-b border-line last:border-0 hover:bg-surface" onClick={() => navigate(`/matters/${m.id}`)}>
                      <td className="px-4 py-2.5">
                        <div className="font-medium text-ink">{m.title}</div>
                        <div className="text-xs text-ink-muted">{query.clientName(db, m.clientId)}</div>
                      </td>
                      <td className="px-4 py-2.5 text-ink-2">{m.court}</td>
                      <td className="px-4 py-2.5 text-ink-2">{m.stage}</td>
                      <td className="px-4 py-2.5 text-ink-2">{formatDate(m.nextHearing)}</td>
                      <td className="px-4 py-2.5"><StatusBadge status={m.status} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Panel>
        </div>

        <div className="space-y-6">
          {/* Priority attention */}
          <Panel>
            <PanelHeader title="Priority Attention" />
            <div className="space-y-2 p-3">
              {priority.slice(0, 6).map((p, i) => (
                <button key={i} onClick={() => navigate(p.to)} className="flex w-full items-start gap-3 rounded-lg border border-line p-3 text-left transition-colors hover:border-copper/40" data-testid={`priority-card-${i}`}>
                  <div className={`mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-${p.tone}-bg`}>
                    <p.icon className={`h-4 w-4 text-${p.tone}`} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-ink">{p.title}</div>
                    <div className="truncate text-xs text-ink-2">{p.sub}</div>
                  </div>
                  <ArrowRight className="h-4 w-4 text-ink-muted" />
                </button>
              ))}
            </div>
          </Panel>

          {/* Activity feed (audit-derived) */}
          <ActivityFeed />
        </div>
      </div>
    </div>
  );
}


/* ---------- Client Portal Dashboard ---------- */
function ClientDashboard({ db, clientScopeId, persona, navigate }) {
  const matters = query.mattersForClient(db, clientScopeId);
  const matterIds = matters.map((m) => m.id);
  const hearings = db.hearings
    .filter((h) => matterIds.includes(h.matterId) && !h.outcome && new Date(h.date) >= new Date())
    .sort((a, b) => new Date(a.date) - new Date(b.date));
  const financeTotals = useFinanceTotals({ clientId: clientScopeId });
  const docs = db.documents.filter((d) => d.clientId === clientScopeId).slice(0, 5);

  return (
    <div>
      <PageHeader title={`Welcome, ${persona.name.split(" ")[0]}`} subtitle="A clear view of your matters, hearings and payments." />

      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <MetricCard testid="client-metric-matters" label="My Matters" value={matters.length} tone="copper" onClick={() => navigate("/matters")} />
        <MetricCard testid="client-metric-hearings" label="Upcoming Hearings" value={hearings.length} tone="info" onClick={() => navigate("/calendar")} />
        <MetricCard testid="client-metric-outstanding" label="Outstanding" value={financeTotals ? ledgerMoney(financeTotals.outstanding) : '—'} tone="warning" onClick={() => navigate("/billing")} />
        <MetricCard testid="client-metric-docs" label="Documents" value={db.documents.filter((d) => d.clientId === clientScopeId).length} tone="plain" onClick={() => navigate("/documents")} />
      </div>

      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          <Panel>
            <PanelHeader title="My Matters"><Button variant="ghost" size="sm" onClick={() => navigate("/matters")}>View all</Button></PanelHeader>
            {matters.length === 0 ? <div className="p-8 text-center text-sm text-ink-2">You have no matters yet.</div> : (
              <div className="divide-y divide-line">
                {matters.map((m) => (
                  <button key={m.id} onClick={() => navigate(`/matters/${m.id}`)} className="flex w-full items-center justify-between px-4 py-3 text-left hover:bg-surface" data-testid={`client-matter-${m.id}`}>
                    <div><div className="font-medium text-ink">{m.title}</div><div className="text-xs text-ink-2">{m.caseNumber} · {m.court} · {m.stage}</div></div>
                    <StatusBadge status={m.status} />
                  </button>
                ))}
              </div>
            )}
          </Panel>

          <Panel>
            <PanelHeader title="Recent Documents"><Button variant="ghost" size="sm" onClick={() => navigate("/documents")}>All documents</Button></PanelHeader>
            {docs.length === 0 ? <div className="p-8 text-center text-sm text-ink-2">No documents shared yet.</div> : (
              <div className="divide-y divide-line">{docs.map((d) => (
                <div key={d.id} className="flex items-center justify-between px-4 py-2.5"><span className="text-sm text-ink">{d.name}</span><span className="text-xs text-ink-2">{formatDate(d.date)}</span></div>
              ))}</div>
            )}
          </Panel>
        </div>

        <div className="space-y-6">
          <Panel>
            <PanelHeader title="Upcoming Hearings" />
            {hearings.length === 0 ? <div className="p-6 text-center text-sm text-ink-2">No upcoming hearings.</div> : (
              <div className="divide-y divide-line">{hearings.map((h) => {
                const m = query.matter(db, h.matterId);
                return (
                  <button key={h.id} onClick={() => navigate(`/matters/${h.matterId}`)} className="block w-full px-4 py-3 text-left hover:bg-surface">
                    <div className="text-sm font-medium text-ink">{formatDate(h.date, "dd MMM")} · {formatTime(h.date)}</div>
                    <div className="truncate text-xs text-ink-2">{m?.title} · {h.court}</div>
                  </button>
                );
              })}</div>
            )}
          </Panel>

          <Panel className="p-4">
            <h3 className="font-display text-base font-semibold text-ink">Payments</h3>
            <div className="mt-2 space-y-1 text-sm">
              <div className="flex justify-between"><span className="text-ink-2">Total Requests</span><span className="font-medium" data-testid="client-ledger-billed">{financeTotals ? ledgerMoney(financeTotals.billed) : '—'}</span></div>
              <div className="flex justify-between"><span className="text-ink-2">Applied Payments / TDS</span><span className="font-medium text-success" data-testid="client-ledger-applied">{financeTotals ? ledgerMoney(financeTotals.billed-financeTotals.outstanding) : '—'}</span></div>
              <div className="flex justify-between"><span className="text-ink-2">Outstanding</span><span className="font-medium text-danger" data-testid="client-ledger-outstanding">{financeTotals ? ledgerMoney(financeTotals.outstanding) : '—'}</span></div>
            </div>
            <Button className="mt-3 w-full" variant="outline" size="sm" onClick={() => navigate("/billing")}>View Payments</Button>
          </Panel>

          <Panel className="p-4">
            <h3 className="font-display text-base font-semibold text-ink">Messages</h3>
            <p className="mt-1 text-sm text-ink-2">Your advocate will post case updates here.</p>
          </Panel>
        </div>
      </div>
    </div>
  );
}
